import React from 'react';
import { getFieldDisplayName } from '../formSchemaUtils';
import { Icon, Mark } from './icons';
import { detailLabel, isBlankBox } from './templateLayout';
import { boxText, isBoxChecked, sampleAnswer, sampleDetail } from './templateValues';

// Helvetica is the face the PDF is set in, so the page on screen measures out
// the way the page on paper will. Kerning is off for the same reason: the PDF's
// built-in fonts can't kern, and a kerned pair such as "To" sets a line most of
// a point narrower on screen than it prints.
const FONT_FAMILY = 'Helvetica, Arial, sans-serif';
const INK = '#111827';

// What fills a box, said in the builder's own voice: the question's name, the
// detail's label.
const sourceName = (box, field) => {
  if (box.kind === 'detail') return detailLabel(box.detail);
  return (field ? getFieldDisplayName(field) : box.fieldName) || 'Question';
};

// One box as it sits on the page. Editing, a box shows what fills it — the
// question's name in purple, set in the box's own type so the layout reads
// true; previewing, it shows a sample answer exactly as it would print, and a
// box that would print nothing shows nothing.
//
// The frame is the box's printed border, if it has one. The builder's own
// outlines and handles are drawn around it by the canvas.
const TemplateBox = ({ box, field, missing, mode, scale, sampleContext }) => {
  const preview = mode === 'preview';
  const line = `${Math.max(1, 0.75 * scale)}px solid ${INK}`;
  const frame = {
    fontFamily: FONT_FAMILY,
    fontKerning: 'none',
    fontSize: box.fontSize * scale,
    lineHeight: 1.2,
    fontWeight: box.bold ? 700 : 400,
    fontStyle: box.italic ? 'italic' : 'normal',
    textAlign: box.align,
    color: INK,
    padding: 2 * scale,
    ...(box.border === 'box' ? { border: line } : {}),
    ...(box.border === 'underline' ? { borderBottom: line } : {}),
  };
  const wraps = box.kind === 'text' || box.format === 'note';

  const centered = (content) => (
    <div style={frame} className="w-full h-full overflow-hidden flex items-center justify-center">
      {content}
    </div>
  );

  // Nothing to print yet: said while editing, blank in the preview.
  if (isBlankBox(box) || missing) {
    if (preview) return <div style={frame} className="w-full h-full" />;
    return centered(
      <span
        className={`truncate italic ${missing ? 'text-red-600' : 'text-gray-400'}`}
        style={{ fontSize: Math.min(box.fontSize, 10) * scale, fontWeight: 400 }}
      >
        {missing ? `Missing: ${box.fieldName || 'question'}` : 'Choose what goes here'}
      </span>,
    );
  }

  if (box.format === 'photo') {
    return (
      <div
        style={frame}
        className="w-full h-full overflow-hidden flex flex-col items-center justify-center gap-1 bg-gray-100 text-gray-400"
      >
        <Icon name="photo" className="w-1/3 max-w-[3rem] h-auto max-h-[60%]" />
        {!preview && (
          <span className="truncate max-w-full text-purple-700" style={{ fontSize: 9 * scale }}>
            {sourceName(box, field)}
          </span>
        )}
      </div>
    );
  }

  if (box.format === 'check') {
    const ticked = preview && isBoxChecked(box, sampleAnswer(box, field));
    return centered(
      <Mark
        mark={box.mark}
        className={`w-[80%] h-[80%] ${preview ? (ticked ? '' : 'invisible') : 'text-purple-400'}`}
      />,
    );
  }

  let label = (box.label || '').trim();
  let value;
  if (box.kind === 'text') {
    value = box.text ? (
      box.text
    ) : (
      !preview && <span className="italic text-gray-400">Type your text in the panel</span>
    );
    label = '';
  } else if (preview) {
    const answer = box.kind === 'detail'
      ? sampleDetail(box.detail, sampleContext)
      : sampleAnswer(box, field);
    value = boxText(box, field, answer);
  } else {
    value = <span className="text-purple-700">{sourceName(box, field)}</span>;
  }

  return (
    <div
      style={frame}
      className={`w-full h-full overflow-hidden flex ${wraps ? 'items-start' : 'items-center'}`}
    >
      <div className={`w-full ${wraps ? 'whitespace-pre-wrap break-words' : 'truncate'}`}>
        {label && <span>{label} </span>}
        {value}
      </div>
    </div>
  );
};

export default TemplateBox;
